package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.EventListener

private const val REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"

/**
 * The small movements that make an interface feel answered rather than merely updated.
 *
 * `Motion` centralises durations, curves and the global CSS `prefers-reduced-motion` rule alongside
 * `transitionColors` / `transitionSurface`. This file holds the small Kotlin-facing pieces that CSS
 * cannot own: truthful static figures, opt-in staggered entrances, and a live Compose view of the
 * reader's motion preference.
 *
 * CSS motion remains a no-op under `prefers-reduced-motion: reduce` through [initLingvoTheme]. Any
 * Kotlin-driven effect must observe [rememberReducedMotion] because a stylesheet cannot cancel a
 * coroutine.
 */

/**
 * A formatted number that is present at its final value on the first frame.
 *
 * Counts used to animate through 24 intermediate values over 720ms. That made a metric lag behind
 * the page that owned it, triggered repeated Compose text updates for motion CSS could not
 * neutralise, and restarted from zero whenever the value changed. Product counts are information,
 * not a celebration: draw the truthful value immediately and keep the tabular figures that prevent
 * the row shifting when it updates.
 */
@Composable
fun LCountUp(
    value: Int,
    modifier: Modifier = Modifier,
    format: (Int) -> String = { it.toString() },
) {
    Span(
        modifier
            .styleModifier { property("font-variant-numeric", "tabular-nums") }
            .toAttrs(),
    ) {
        Text(format(value))
    }
}

/**
 * Staggered entrance for a list of cards.
 *
 * One container fading in reads as a page that was slow. The same content arriving in sequence
 * reads as a page being assembled — the difference is about 60ms per item, and after roughly six
 * items the delay stops helping and starts being a wait, so it caps.
 */
fun Modifier.enterStaggered(index: Int, stepMs: Int = 60, maxSteps: Int = 6): Modifier =
    // **Never for a reader who asked for less movement.** The global rule collapses animation
    // *duration* to 0.01ms but leaves the delay, so an element starting at opacity 0 with `both`
    // can still be held invisible for its stagger. Not applying the animation at all is the honest
    // reading of the preference: the content is simply there.
    if (prefersReducedMotion()) {
        this
    } else {
        styleModifier {
            property("animation", "lingvo-enter ${Motion.StandardMs}ms ${Motion.EaseOut} both")
            property("animation-delay", "${minOf(index, maxSteps) * stepMs}ms")
        }
    }

/**
 * A single element's entrance, for content that arrives on its own rather than in a list.
 *
 * The same 6px rise as [enterStaggered] with no delay — an async panel settling into place, a
 * confirmation appearing beside the action that caused it. Separate from [enterStaggered] only so a
 * call site does not have to pass a meaningless `index = 0` and imply a sequence that is not there.
 *
 * Transform only, and skipped entirely under a reduced-motion preference, for the reasons
 * [enterStaggered] documents: a keyframe that opens at `opacity: 0` under `both` leaves its content
 * invisible in a tab the browser has stopped painting.
 */
fun Modifier.enterRise(): Modifier =
    if (prefersReducedMotion()) {
        this
    } else {
        styleModifier {
            property("animation", "lingvo-enter ${Motion.StandardMs}ms ${Motion.EaseOut} both")
        }
    }

/**
 * The reader's live motion preference as Compose state.
 *
 * CSS transitions are neutralised by the global media rule in [initLingvoTheme], but Kotlin-driven
 * effects do not see that rule. Listening to the same `MediaQueryList` keeps those effects honest
 * when the preference changes while the page is already open, and removing the listener with the
 * composition prevents a discarded component from retaining state it can no longer display.
 */
@Composable
fun rememberReducedMotion(): Boolean {
    val mediaQuery = remember { window.matchMedia(REDUCED_MOTION_QUERY) }
    var reducedMotion by remember(mediaQuery) { mutableStateOf(mediaQuery.matches) }

    DisposableEffect(mediaQuery) {
        val listener = EventListener { reducedMotion = mediaQuery.matches }
        mediaQuery.addEventListener("change", listener)
        onDispose { mediaQuery.removeEventListener("change", listener) }
    }

    return reducedMotion
}

/**
 * Whether this reader has asked for less movement.
 *
 * This snapshot is for non-composable modifier construction such as [enterStaggered]. Kotlin-driven
 * effects must use [rememberReducedMotion] so a preference change while the page is open cancels
 * their work instead of waiting for another composition.
 */
fun prefersReducedMotion(): Boolean =
    runCatching { window.matchMedia(REDUCED_MOTION_QUERY).matches }.getOrDefault(false)

/** How long a copy confirmation stays on screen before the control returns to its resting state. */
private const val COPY_CONFIRMED_HOLD_MS = 1600

/**
 * A copy control's own answer that it worked.
 *
 * Copying announced itself to screen readers and to nobody else. Every copy control in the system
 * swapped nothing, moved nothing and stayed exactly as it was, so a sighted reader's only evidence
 * was to paste somewhere and look — and the fix kept being pushed to call sites, which solved it
 * eight different ways: three raised a toast, two cleared an unrelated message, one set a `copied`
 * flag it then rendered itself, and the rest did nothing. `LCopyField`'s own KDoc rules out the
 * toast ("deliberately not a toast"), correctly, and then left the gap open.
 *
 * So the confirmation belongs to the control. `onCopied` stays for what a *page* wants to do in
 * addition, and no longer carries the burden of being the only feedback there is.
 *
 * Under `prefers-reduced-motion` the global rule in [initLingvoTheme] flattens the transition and
 * the check still appears: the movement is the decoration, the glyph is the information, and only
 * one of those is safe to drop.
 */
@Stable
class CopyConfirmation internal constructor(
    private val scope: CoroutineScope,
    private val holdMs: Int,
) {
    var confirmed by mutableStateOf(false)
        private set

    private var reset: Job? = null

    /** Show the confirmation, restarting the hold if it is already showing. */
    fun confirm() {
        // Cancelled rather than left to expire, so copying twice in quick succession does not have
        // the first hold end the second confirmation early.
        reset?.cancel()
        confirmed = true
        reset = scope.launch {
            delay(holdMs.toLong())
            confirmed = false
        }
    }
}

/** Remembers a [CopyConfirmation] bound to this composition's lifetime. */
@Composable
fun rememberCopyConfirmation(holdMs: Int = COPY_CONFIRMED_HOLD_MS): CopyConfirmation {
    val scope = rememberCoroutineScope()
    return remember(scope, holdMs) { CopyConfirmation(scope, holdMs) }
}

/**
 * The copy glyph, which becomes a check while [confirmed] and returns on its own.
 *
 * Both glyphs stay in the DOM and cross-fade, rather than one replacing the other. Swapping the
 * child would mount a fresh element, and an element cannot transition from a state it was never in
 * — the check would simply appear, which is the thing this exists to improve on. Scale runs on the
 * emphasized curve so the check arrives with a little authority; opacity runs on the plain ease-out
 * so the outgoing glyph does not linger under the incoming one.
 */
@Composable
fun LCopyGlyph(
    confirmed: Boolean,
    modifier: Modifier = Modifier,
    size: CSSSizeValue<CSSUnit.px> = IconSize.MD,
) {
    Span(
        modifier
            .iconSize(size)
            .styleModifier {
                property("position", "relative")
                property("display", "inline-flex")
            }
            .toAttrs {
                attr("data-copy-glyph", if (confirmed) "confirmed" else "idle")
            },
    ) {
        Span(
            copyGlyphLayer(visible = !confirmed).toAttrs { attr("data-copy-glyph-layer", "copy") },
        ) { LCopyIcon(Modifier.iconSize(size)) }
        Span(
            copyGlyphLayer(visible = confirmed).toAttrs { attr("data-copy-glyph-layer", "confirmed") },
        ) { LCheckIcon(Modifier.iconSize(size)) }
    }
}

private fun copyGlyphLayer(visible: Boolean): Modifier = Modifier.styleModifier {
    property("position", "absolute")
    property("inset", "0")
    property("display", "inline-flex")
    property("opacity", if (visible) "1" else "0")
    // `GlyphEnterScale`, not a 28% jump, and `EaseOut` on both layers.
    //
    // `EaseEmphasized` is reserved in writing for the single orchestrated moment on the landing
    // page — a copy button is the opposite of that, reached repeatedly during ordinary work on
    // three separate controls. Borrowing the signature curve here spends it. And the two in-place
    // glyph swaps in this system now start from the same distance instead of disagreeing by a
    // literal nobody chose twice.
    property("transform", if (visible) "scale(1)" else "scale(${Motion.GlyphEnterScale})")
    property(
        "transition",
        "opacity ${Motion.FastMs}ms ${Motion.EaseOut}, " +
            "transform ${Motion.FastMs}ms ${Motion.EaseOut}",
    )
}

/**
 * Whether an ancestor has already animated this content's arrival.
 *
 * **The nesting problem, solved once instead of per call site.** `LEmptyState` and `LAlert` apply
 * [enterRise] themselves, because they appear standalone far more often than they appear inside
 * something. But `LAsyncContent` animates its success slot too — so a panel whose success arm is
 * nothing but an empty state rose 6px inside another element rising 6px, and the movement
 * compounded into something neither component intended.
 *
 * The obvious repairs are both wrong. Dropping the container's entrance leaves every *ordinary*
 * slot arriving with nothing, which is most of them. Dropping the components' own entrance leaves
 * them flat everywhere they are used alone, which is most of where they are used. The information
 * that resolves it is only available at the join: whether something above has already said "this
 * arrived".
 *
 * Default `false`, so a component used on its own is unaffected and nothing changes for the many
 * call sites that never nest.
 */
val LocalEntranceOwned = compositionLocalOf { false }

/**
 * [enterRise], unless an ancestor was already animating this arrival when this element appeared.
 *
 * For a component that animates itself and can also be handed to a container that animates. Read
 * from composition rather than passed as a parameter, because the call site between the two —
 * `LAsyncContent(state) { LEmptyState(...) }` — has no way to know it is the join.
 *
 * **Decided once, at first composition, and never revisited.** Ownership expires — it describes an
 * arrival, not a container — so reading it live meant an element that had been sitting on screen
 * for the whole entrance suddenly gained an entrance of its own the instant the flag flipped back.
 * An arrival that has already happened cannot be re-announced, and `remember` is what says so: the
 * question is "was this arrival owned when I appeared", which has one answer for the life of the
 * element.
 */
@Composable
fun Modifier.enterRiseUnlessOwned(): Modifier {
    val owned = LocalEntranceOwned.current
    val ownedOnArrival = remember { owned }
    return if (ownedOnArrival) this else enterRise()
}

/**
 * Whether a wait has lasted long enough to be worth showing.
 *
 * False while [active] has been true for less than [afterMs], so a wait that resolves inside the
 * grace period is never painted at all. The reader goes from the last real frame to the next one
 * without a screen appearing in between.
 *
 * **The failure this removes is a flicker, not a delay.** A session that is already cached resolves
 * in a frame or two, and the screens shown meanwhile — the sign-in handoff with its rotating
 * wordmark, the whole app-shell skeleton — were being mounted and torn away inside 80ms. That is
 * long enough to see and far too short to read, so it costs attention and returns nothing.
 *
 * Resets whenever [active] goes false, so a second wait gets its own grace rather than inheriting a
 * timer that already expired.
 *
 * ```
 * val settled = rememberSettledWait(session is SessionState.Loading)
 * if (settled) AuthHandoff("Checking your saved session.")
 * ```
 */
@Composable
fun rememberSettledWait(active: Boolean, afterMs: Int = Motion.WaitGraceMs): Boolean {
    var elapsed by remember { mutableStateOf(false) }
    LaunchedEffect(active, afterMs) {
        if (!active) {
            elapsed = false
            return@LaunchedEffect
        }
        delay(afterMs.toLong())
        elapsed = true
    }
    return active && elapsed
}
